import { closestCenter, DndContext, type DragEndEvent } from '@dnd-kit/core';
import { restrictToParentElement, restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { Link } from '@tanstack/react-router';
import type { CustomFieldDTO } from '@tempo-io/item-store-dto';
import { Button } from '@tempo-io/sui-button';
import { Tune } from '@tempo-io/sui-icons';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@tempo-io/sui-tooltip';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createDragSensors } from '@/components/common/settings/item-types/lib/utils';
import type { CustomField } from '@/lib/api/types/customField';
import type { Item } from '@/lib/api/types/items';
import { getDndUpdateBody } from '@/lib/dnd/helpers';
import { useOrgName } from '@/lib/hooks/common/useOrgName';
import { usePermissions } from '@/lib/hooks/common/usePermissions';
import { useUpdateCustomFieldMutation } from '@/lib/mutations/common/custom-field/useUpdateCustomFieldMutation';
import { useItemTypeFieldsQuery } from '@/lib/queries/common/useItemTypeFieldsQuery';
import type { CustomFieldsTabAdapter } from '../../adapters/types';
import { useTabAdapter } from '../../context/TabAdapterContext';
import { useItemTypesByIdQueryWithPublished } from '../../hooks/useItemTypesQueryWithPublished';
import { CustomFieldValueWithAdapter } from './CustomFieldValueWithAdapter';
import { SortableCustomField } from './SortableCustomField';

const PANEL_QUERY_OPTIONS = {
  refetchOnMount: false,
} as const;

const EMPTY_FIELDS: CustomField[] = [];

interface CustomFieldsProps {
  item: Item;
  customFieldDefinitions?: CustomFieldDTO[];
}

export const CustomFields = ({
  item,
  customFieldDefinitions: customFieldDefinitionsProp,
}: CustomFieldsProps) => {
  const { t } = useTranslation();
  const adapter = useTabAdapter<CustomFieldsTabAdapter>();
  const { data: fetchedDefinitions = EMPTY_FIELDS, isFetched: queryFetched } =
    useItemTypeFieldsQuery(item.itemTypeId, {
      ...PANEL_QUERY_OPTIONS,
      enabled: !customFieldDefinitionsProp,
    });
  const customFieldDefinitions =
    (customFieldDefinitionsProp as CustomField[] | undefined) ?? fetchedDefinitions;
  const isFetched = !!customFieldDefinitionsProp || queryFetched;
  const { data: itemTypesById = {} } = useItemTypesByIdQueryWithPublished({
    ...PANEL_QUERY_OPTIONS,
    enabled: !customFieldDefinitionsProp,
  });
  const org = useOrgName();
  const permissions = usePermissions();
  const canEditIntegrations = permissions.edit_integrations ?? false;
  const updateCustomFieldMutation = useUpdateCustomFieldMutation();
  const sensors = createDragSensors();

  const [reorderableFields, setReorderableFields] = useState<CustomField[]>(() =>
    customFieldDefinitions.filter((f) => adapter.isVisible(f.id.toString())),
  );

  useEffect(() => {
    setReorderableFields(customFieldDefinitions.filter((f) => adapter.isVisible(f.id.toString())));
  }, [customFieldDefinitions, adapter]);

  const handleDragEnd = (event: DragEndEvent) => {
    const body = getDndUpdateBody<CustomField>({
      entries: reorderableFields,
      setEntries: setReorderableFields,
      setActiveId: () => {},
      getId: (field) => field.id,
      event,
    });

    if (body) {
      updateCustomFieldMutation.mutate({ customFieldId: body.id, data: body.data });
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-row items-center justify-between text-lg font-bold text-text-neutral-secondary">
        <div className="flex flex-row items-center gap-1 min-w-0 truncate text-text-brand-primary font-sans text-base font-medium leading-5 tracking-normal">
          {t(($) => $.adaptivePlanner.itemPanel.customFields.label)}
        </div>
        {!customFieldDefinitionsProp && canEditIntegrations && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="outline"
                  size="icon"
                  asChild
                  aria-label={t(($) => $.adaptivePlanner.itemPanel.customFieldsSection.settings)}
                >
                  <Link to="/org/$org/settings/custom-fields" params={{ org }}>
                    <Tune className="w-4 h-4" />
                  </Link>
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                {t(($) => $.adaptivePlanner.itemPanel.customFieldsSection.settings)}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
      </div>

      {isFetched && reorderableFields.length === 0 ? (
        <p className="text-sm text-text-neutral-secondary">
          {t(($) => $.adaptivePlanner.itemPanel.customFields.noFieldsForItemType)}
        </p>
      ) : (
        <DndContext
          autoScroll={false}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
          sensors={sensors}
          modifiers={[restrictToParentElement, restrictToVerticalAxis]}
        >
          <SortableContext
            items={reorderableFields.map((f) => f.id)}
            strategy={verticalListSortingStrategy}
          >
            <div className="flex flex-col gap-4">
              {reorderableFields.map((customFieldDef) => {
                const fieldId = customFieldDef.id.toString();
                const fieldValue = item.customFields?.[fieldId];
                const isEditable = adapter.isEditable(fieldId);

                return (
                  <SortableCustomField
                    key={fieldId}
                    fieldId={customFieldDef.id}
                    isDraggable={isEditable}
                  >
                    <div className="mb-2 last:mb-0">
                      <CustomFieldValueWithAdapter
                        customField={customFieldDef}
                        value={fieldValue}
                        itemTypesById={itemTypesById}
                        isEditable={isEditable}
                        onUpdate={adapter.updateFieldValue}
                      />
                    </div>
                  </SortableCustomField>
                );
              })}
            </div>
          </SortableContext>
        </DndContext>
      )}
    </div>
  );
};
